<template>
  <el-card shadow="hover" class="material-card" :class="{ 'is-selected': selectable && selected, 'is-role': isRole }">
    <!-- 批量选择：整张卡片可点（不必去瞄那个小勾选框）。
         选择态时盖一层透明遮罩，把预览图/下载/播放等交互全挡住，
         免得想选中却误触了别的按钮。遮罩 z-index 低于勾选框，勾选框仍可单独点。 -->
    <div
      v-if="selectable"
      class="material-pick"
      @click="$emit('select', material, !selected)"
    ></div>
    <div v-if="selectable" class="material-check" @click.stop>
      <el-checkbox :value="selected" @change="$emit('select', material, $event)" />
    </div>
    <!-- ========== 角色卡片：图在上、名字在下 ==========
         角色是平台预置的公共资源，卡片上不放类型标签/编号/角色标签/下载/删除，
         只留一只小圆钮（悬停变实），点它或直接点图都能翻看各个角度的图。 -->
    <template v-if="isRole">
      <div class="role-cover">
        <el-image
          ref="roleImg"
          :src="thumbUrl(material.file_path, 640)"
          :preview-src-list="previewSrcs"
          fit="contain"
          class="role-img"
        />
        <div
          v-if="previewSrcs.length > 1"
          class="role-view-btn"
          :title="'檢視各面圖'"
          @click.stop="openGroup"
        >
          <i class="el-icon-picture-outline"></i>
        </div>
      </div>
      <div class="role-name" :title="title || fileName">{{ title || fileName }}</div>
    </template>

    <template v-else>
      <div class="material-type">
        <div>
          <el-tag size="mini" :type="typeColor">{{ typeLabel }}</el-tag>
          <el-tag v-if="material.redo_from_id" size="mini" type="warning" style="margin-left:4px">二次修改</el-tag>
        </div>
        <span class="muted">#{{ material.id }}</span>
      </div>
      <div class="material-path" :title="title || material.file_path">
        {{ title || fileName }}
      </div>
      <!-- 素材标签（角色卡走上面的分支，不显示标签行） -->
      <div v-if="material.tag_list && material.tag_list.length" class="material-tags">
        <el-tag v-for="t in material.tag_list" :key="t" size="mini" type="info">{{ t }}</el-tag>
      </div>
      <div v-if="material.feedback" class="material-feedback muted" :title="material.feedback">
        修改意見：{{ material.feedback }}
      </div>
      <div v-if="isImage" class="material-preview">
        <!-- 列表预览用缩略图（原图很大，回显慢）；点开大图仍看原图 -->
        <el-image
          :src="thumbUrl(material.file_path, 480)"
          :preview-src-list="previewSrcs"
          fit="contain"
          class="material-img"
        />
      </div>
      <div v-else-if="isAudio" class="material-preview audio-preview">
        <audio :src="fileUrl(material.file_path)" controls preload="metadata" class="material-audio"></audio>
      </div>
      <div v-else-if="isVideo" class="material-preview video-preview" @click="videoVisible = true">
        <video
          :src="fileUrl(material.file_path)"
          preload="metadata"
          muted
          playsinline
          class="material-video"
        ></video>
        <i class="el-icon-video-play"></i>
      </div>
      <div v-else class="material-preview">
        <span class="muted">無預覽</span>
      </div>
      <div class="material-meta muted" v-if="material.model_used">
        模型: {{ material.model_used }}
      </div>
      <div class="material-actions">
        <el-button type="text" size="mini" icon="el-icon-download" @click="onDownload">
          下載
        </el-button>
        <el-button v-if="exportable && !isVideo && !isAudio" type="text" size="mini" icon="el-icon-upload2" :loading="exporting" @click="onExportPsd">
          匯出PSD
        </el-button>
        <!-- 删除：本组件只发事件，确认弹窗与实际调用由父级统一处理。
             必须判 deletable —— 任务中心也复用这个卡片，那里没有删除功能，
             不判就会留一个"点了没反应"的死按钮。 -->
        <el-button v-if="deletable" type="text" size="mini" icon="el-icon-delete" @click="$emit('delete', material)">
          刪除
        </el-button>
      </div>

    </template>

    <el-dialog
      v-if="isVideo"
      :visible.sync="videoVisible"
      :append-to-body="true"
      :width="videoBox.width + 32 + 'px'"
      top="6vh"
      custom-class="video-play-dialog"
    >
      <div class="video-dialog-body" :style="{ width: videoBox.width + 'px', height: videoBox.height + 'px' }">
        <video
          v-if="videoVisible"
          :src="fileUrl(material.file_path)"
          controls
          autoplay
          class="video-dialog-player"
          @loadedmetadata="onVideoMeta"
        ></video>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import { fileUrl, thumbUrl, downloadUrl } from '../utils/file'
import { exportPsd } from '../api/materials'

export default {
  name: 'MaterialCard',
  props: {
    material: { type: Object, required: true },
    // 卡片主标题（把文件名换成更好读的名字，如角色名）；不传就还是显示文件名
    title: { type: String, default: '' },
    // 批量选择态：只有列表进入"批量删除"模式时才显示勾选框
    selectable: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    // 是否显示删除按钮：只有素材库有删除功能，任务中心等复用方不显示
    deletable: { type: Boolean, default: false },
    // 是否显示「导出PSD」：对角色这类"不是生成产物"的卡片没意义，可由调用方关掉
    exportable: { type: Boolean, default: true }
  },
  data() {
    return {
      exporting: false,
      videoVisible: false,
      videoBox: { width: 640, height: 360 }   // 播放框尺寸，随视频实际比例自适应
    }
  },
  computed: {
    isRole() {
      return this.material.type === 'role'
    },
    isImage() {
      const p = (this.material.file_path || '').toLowerCase()
      return /\.(png|jpe?g|webp|gif|bmp)$/.test(p)
    },
    /**
     * 大图预览的图列表。
     *
     * 常规素材卡片只有这一张；但「角色」这类**整组图**的卡片会额外带 preview_paths
     * （封面 + 整组参考图），那时点开就能左右翻看整组，而不是只看得到封面。
     */
    previewSrcs() {
      const list = this.material && this.material.preview_paths
      if (Array.isArray(list) && list.length) {
        return list.map(p => fileUrl(p))
      }
      return [fileUrl(this.material.file_path)]
    },
    isVideo() {
      const p = (this.material.file_path || '').toLowerCase()
      return /\.(mp4|mov|m4v|webm|avi|mkv)$/.test(p)
    },
    isAudio() {
      const p = (this.material.file_path || '').toLowerCase()
      return /\.(mp3|wav|m4a|aac|ogg)$/.test(p)
    },
    fileName() {
      const p = this.material.file_path || ''
      const i = Math.max(p.lastIndexOf('/'), p.lastIndexOf('\\'))
      return i >= 0 ? p.slice(i + 1) : p
    },
    /** 上传素材的 type 是 image/video，展示成中文更好读 */
    typeLabel() {
      const map = { image: '圖片', video: '影片', audio: '音訊', role: '角色' }
      return map[this.material.type] || this.material.type
    },
    typeColor() {
      // promotion 与 promotion_image 两个 key 都要留：早期前端提交的类型值是 promotion，
      // 后来统一成 promotion_image，库里两批数据都有 —— 删掉任一 key 都会让那批素材的
      // 类型标签掉成灰色。
      const map = {
        video: 'danger',
        image: 'primary',
        promotion: 'warning',
        promotion_image: 'warning',
        sku_image: 'warning',
        detail_image: 'success',
        main_image: 'primary',
        scene_image: 'danger'
      }
      return map[this.material.type] || 'info'
    }
  },
  methods: {
    /**
     * 点小圆钮看各面图。
     *
     * el-image 的预览由它内部的 clickHandler 打开（打开后自带左右翻页），
     * 这里只是把它转发一下，让按钮和点图行为一致 —— 不自己再实现一套预览。
     * 万一拿不到就什么都不做，点图本身仍然能打开。
     */
    openGroup() {
      const img = this.$refs.roleImg
      if (img && typeof img.clickHandler === 'function') {
        img.clickHandler()
      }
    },
    fileUrl,
    thumbUrl,
    onVideoMeta(e) {
      const v = e.target
      const w = v && v.videoWidth
      const h = v && v.videoHeight
      if (!w || !h) return
      // 播放框保持视频原始比例，限制在 760×760 范围内，任意比例都适配
      const scale = Math.min(760 / w, 760 / h, 1)
      this.videoBox = { width: Math.round(w * scale), height: Math.round(h * scale) }
    },
    onDownload() {
      const a = document.createElement('a')
      a.href = downloadUrl(this.material.file_path)
      a.download = ''
      document.body.appendChild(a)
      a.click()
      a.remove()
    },
    async onExportPsd() {
      if (this.exporting) return
      this.exporting = true
      try {
        const data = await exportPsd(this.material.id)
        if (data.psd) {
          // 直接触发 PSD 下载（用 <a> 触发，避免被浏览器拦截弹窗）
          const a = document.createElement('a')
          a.href = downloadUrl(data.psd)
          a.download = ''
          document.body.appendChild(a)
          a.click()
          a.remove()
          // 透明底 PNG 若生成也一并下载
          if (data.transparent) {
            const t = document.createElement('a')
            t.href = downloadUrl(data.transparent)
            t.download = ''
            document.body.appendChild(t)
            t.click()
            t.remove()
          }
          const extra = data.transparent ? '，透明底 PNG 已一併下載' : ''
          this.$message.success('PSD 已產生並開始下載' + extra)
        } else {
          this.$message.warning(data.error || '匯出失敗：未產生 PSD 檔案')
        }
      } catch (e) {
        this.$message.error(e.message || '匯出 PSD 失敗')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.material-card {
  margin-bottom: 12px;
  /* 勾选框要绝对定位到卡片角上 */
  position: relative;
}
/* 选择态遮罩：盖住整张卡片（含预览图与各个按钮），点哪里都算选中/取消 */
.material-pick {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  cursor: pointer;
  border-radius: 4px;
}
.material-card.is-selected .material-pick {
  background: rgba(245, 108, 108, 0.06);
}
/* 勾选框浮在左上角，垫一层浅底避免压在预览图上看不清；
   z-index 必须高于遮罩，否则点不到它 */
.material-check {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 3;
  padding: 2px 4px;
  border-radius: 4px;
  /* 压在预览图上：深色半透明垫底，浅图深图都能看清勾选框，也不在深色卡片上留白块 */
  background: rgba(11, 18, 32, 0.45);
}
/* 选中态用红色描边：删除不可恢复，让"哪些会被删掉"一眼可见 */
.material-card.is-selected {
  border-color: #f56c6c;
}
.material-type {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
/* 角色标签（只有角色卡片带 tag_list） */
.material-tags {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.material-path {
  margin-top: 8px;
  font-size: 12px;
  color: var(--c-text-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.material-feedback {
  margin-top: 4px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.material-preview {
  margin-top: 8px;
  height: 120px;
  border: 1px dashed var(--c-border-2);
  border-radius: var(--r-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--c-surface-2);
  position: relative;
}
/* 音频：给个播放器，不用图片/视频那套占位 */
.audio-preview { border-style: solid; }
.material-audio { width: 100%; height: 40px; }

.video-preview i {
  position: absolute;
  font-size: 36px;
  color: var(--c-text-3);
  pointer-events: none;
}
.material-img,
.material-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.img-error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 28px;
  color: var(--c-text-3);
}
.material-meta {
  margin-top: 6px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 动作按钮：显式横排（原来只写 text-align，窄卡片里会折成竖排） */
.material-actions {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;
}
/* Element 默认给相邻按钮 margin-left，改用 gap 后排掉 */
.material-actions ::v-deep .el-button + .el-button {
  margin-left: 0;
}
.video-dialog-body {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}
.video-dialog-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.muted {
  color: var(--c-text-3);
  font-size: 12px;
}
/* ========== 角色卡片 ========== */
/* 3:4 竖版占位：用 padding-top 撑出比例，人像正好填满，不用写死高度，
   也不会像固定高度那样在竖图两侧留一大片空白 */
.role-cover {
  position: relative;
  width: 100%;
  padding-top: 133%;
  border-radius: var(--r-sm);
  overflow: hidden;
  background: var(--c-surface-2);
}
.role-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
/* 查看各面图的小圆钮：平时半透明，悬停整张卡片时变实 */
.role-view-btn {
  position: absolute;
  right: 6px;
  bottom: 6px;
  z-index: 2;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0.5;
  transition: opacity 0.2s;
}
.material-card:hover .role-view-btn {
  opacity: 1;
}
.role-name {
  margin: 8px 0 2px;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: var(--c-text-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>
